/** Merge ODM XML fragments by OID — used by content tabs and build benchmarks. */

export interface MergeXmlResult {
  result: string
  merged: boolean
  error?: string
  mergedCount?: number
}

function appendBeforeClosingTag(
  existingXml: string,
  fragmentXml: string,
): MergeXmlResult {
  const closingTag = '</MetaDataVersion>'
  const idx = existingXml.lastIndexOf(closingTag)
  if (idx !== -1) {
    const result = existingXml.slice(0, idx) + '\n' + fragmentXml.trim() + '\n' + existingXml.slice(idx)
    return { result, merged: true }
  }
  return { result: fragmentXml.trim(), merged: true }
}

/** Minimal, non-spec-authoritative recovery wrapper -- this tab is a live
 * preview only; the server pins its own shell independently (see
 * status_registry.get_or_store_shell_xml) and never trusts this tab's
 * content for the real build. Its only job is to give a tab that's stuck on
 * an unwrapped bag of top-level elements (no shared <MetaDataVersion> root --
 * confirmed live 2026-07-24, e.g. a raw FormDef+ItemGroupDef+CodeList
 * sequence with zero <ODM>/<Study>/<MetaDataVersion> tags anywhere) one
 * single root to hang off of, so later merges have something to parse and
 * a </MetaDataVersion> to anchor on instead of failing forever. */
function wrapUnrootedContent(existingXml: string): string {
  const trimmed = existingXml.trim()
  if (!trimmed) {
    return '<ODM><Study><MetaDataVersion></MetaDataVersion></Study></ODM>'
  }
  return `<ODM><Study><MetaDataVersion>\n${trimmed}\n</MetaDataVersion></Study></ODM>`
}

/** Elements the ODM spec gives no OID attribute at all, so they can never
 * be picked up by the '[OID]'-based per-element merge loop below — each
 * study has exactly one, keyed by tag name rather than OID. Confirmed live
 * 2026-07-28 on EVOLVE-EP: a fragment containing these mixed with
 * OID-bearing elements (e.g. StudyEventGroupDef) silently dropped Protocol
 * and GlobalVariables across 3 separate send attempts — the '[OID]' query
 * never selected them, and because the OID-bearing sibling content DID
 * merge, the function returned success without ever falling back to
 * appendBeforeClosingTag either. */
const SINGLETON_PARENT_MAP: Record<string, string> = {
  GlobalVariables: 'Study',
  Protocol: 'MetaDataVersion',
}

function mergeSingletonElements(fragmentDoc: Document, existingDoc: Document): number {
  let merged = 0
  for (const [tagName, parentTag] of Object.entries(SINGLETON_PARENT_MAP)) {
    const fragmentEl = fragmentDoc.querySelector(tagName)
    if (!fragmentEl) continue
    const parent = existingDoc.querySelector(parentTag)
    if (!parent) continue
    const existingEl = Array.from(parent.children).find(child => child.tagName === tagName)
    const imported = existingDoc.importNode(fragmentEl, true)
    if (existingEl) {
      parent.replaceChild(imported, existingEl)
    } else {
      parent.appendChild(imported)
    }
    merged++
  }
  return merged
}

/** The ODM root's own required attributes (xmlns, ODMVersion, FileOID,
 * FileType, ...) live on the <ODM> tag itself, not on any child element --
 * '[OID]' can never select them either. If the LLM sends a corrective
 * fragment wrapped in its own <ODM ...> root, copy those attributes onto
 * the existing document's real root instead of letting them get parsed
 * and discarded. */
function mergeRootAttributes(fragmentDoc: Document, existingDoc: Document): number {
  const fragmentRoot = Array.from(fragmentDoc.documentElement.children).find(child => child.tagName === 'ODM')
  if (!fragmentRoot || fragmentRoot.attributes.length === 0) return 0
  const existingRoot = existingDoc.documentElement
  if (existingRoot.tagName !== 'ODM') return 0
  for (const attr of Array.from(fragmentRoot.attributes)) {
    existingRoot.setAttribute(attr.name, attr.value)
  }
  return 1
}

export function mergeXmlFragment(existingXml: string, fragmentXml: string): MergeXmlResult {
  try {
    const parser = new DOMParser()
    const serializer = new XMLSerializer()

    let existingDoc = parser.parseFromString(existingXml, 'text/xml')
    let existingError = existingDoc.querySelector('parsererror')
    if (existingError) {
      // The existing tab content itself may just be missing a shared root
      // (a flat sequence of otherwise well-formed elements) rather than
      // being genuinely malformed -- try wrapping it once before giving up.
      const rewrapped = wrapUnrootedContent(existingXml)
      const rewrappedDoc = parser.parseFromString(rewrapped, 'text/xml')
      const rewrappedError = rewrappedDoc.querySelector('parsererror')
      if (rewrappedError) {
        const detail = existingError.textContent?.slice(0, 200) ?? 'unknown parse error'
        return { result: existingXml, merged: false, error: `Existing XML is malformed: ${detail}` }
      }
      existingDoc = rewrappedDoc
      existingError = null
    }

    const fragmentDoc = parser.parseFromString(`<root>${fragmentXml}</root>`, 'text/xml')
    const fragmentError = fragmentDoc.querySelector('parsererror')
    if (fragmentError) {
      return appendBeforeClosingTag(existingXml, fragmentXml)
    }

    const fragmentElements = fragmentDoc.querySelectorAll('[OID]')
    let mergedCount = 0
    mergedCount += mergeSingletonElements(fragmentDoc, existingDoc)
    mergedCount += mergeRootAttributes(fragmentDoc, existingDoc)

    const parentMap: Record<string, string> = {
      StudyEventDef: 'MetaDataVersion',
      FormDef: 'MetaDataVersion',
      ItemGroupDef: 'MetaDataVersion',
      ItemDef: 'MetaDataVersion',
      CodeList: 'MetaDataVersion',
      ConditionDef: 'MetaDataVersion',
      MethodDef: 'MetaDataVersion',
      MeasurementUnit: 'BasicDefinitions',
      StudyEventGroupDef: 'MetaDataVersion',
    }

    fragmentElements.forEach(fragmentEl => {
      const oid = fragmentEl.getAttribute('OID')
      const tagName = fragmentEl.tagName
      if (!oid) return

      const existingEl = existingDoc.querySelector(`${tagName}[OID="${oid}"]`)
      if (existingEl?.parentNode) {
        existingEl.parentNode.replaceChild(existingDoc.importNode(fragmentEl, true), existingEl)
        mergedCount++
      } else {
        const parentTag = parentMap[tagName] || 'MetaDataVersion'
        const parent = existingDoc.querySelector(parentTag)
        if (parent) {
          parent.appendChild(existingDoc.importNode(fragmentEl, true))
          mergedCount++
        }
      }
    })

    if (mergedCount === 0) {
      return appendBeforeClosingTag(existingXml, fragmentXml)
    }

    let result = serializer.serializeToString(existingDoc)
    result = result.replace(/(<\?xml[^?]*\?>\s*)+/g, '<?xml version="1.0" encoding="UTF-8"?>\n')
    result = result.replace(/ xmlns=""/g, '')
    return { result, merged: true, mergedCount }
  } catch {
    return appendBeforeClosingTag(existingXml, fragmentXml)
  }
}
